import { useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Empty, Table, TableProps } from 'antd';
import { Link } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onDeleteRegion, onGetRegions } from 'modules/regions/services/regions';

// models
import { Region } from 'modules/regions/models/region';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import Icon from 'utils/icon';
import AuthActionComponent from 'components/authAction/authAction';
import NoData from 'components/noData/noData';
import { useTranslation } from 'react-i18next';
import { isRTL } from 'core/services/language';

function ListRegionsPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [regions, setRegions] = useState<Region[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [tempDeletedRegion, setTempDeletedRegion] = useState<number>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<Region>['columns'] = [
		{
			title: t('REGIONS.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name) => <p className="mb-0 fw-medium">{isRTL() ? name?.ar : name?.en}</p>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'active',
			key: 'active',
			render: (active) => (
				<span className={`status fw-medium ${active ? 'active' : 'inactive'}`}>{active ? t('COMMON.ACTIVE') : t('COMMON.INACTIVE')}</span>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, region) => (
				<div className="d-flex gap-2">
					<AuthActionComponent action="region_update">
						<Link to={`${region.id}`}>
							<Icon name="edit" width={35} height={35} />
						</Link>
					</AuthActionComponent>
					<AuthActionComponent action="region_delete">
						<Button type="link" className="p-0" onClick={() => onShowConfirmation(region.id)}>
							<Icon name="delete" width={35} height={35} />
						</Button>
					</AuthActionComponent>
				</div>
			),
		},
	];

	useEffect(() => {
		getRegions();
	}, []);

	const getRegions = async () => {
		try {
			setIsLoading(true);

			const response = await onGetRegions();
			setRegions(response.data.data.regions);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			setIsLoading(false);
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetRegions(page);
			setRegions(response.data.data.regions);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const onShowConfirmation = (roleId: number) => {
		setShowConfirmation(true);
		setTempDeletedRegion(roleId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteRegion(tempDeletedRegion as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('REGIONS.DELETED_SUCCESS'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getRegions();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.OPS'), t('REGIONS.DELETE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('REGIONS.MANAGEMENT_TITLE')}</h3>
				{/* <p>The most important info highlights</p> */}
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className={`sectionContainer listData`}>
					<header>
						<div>
							<h5>
								{totalRecords} {t('REGIONS.MANAGEMENT_COUNT')}
							</h5>
							<p>{t('REGIONS.MANAGEMENT_DESCRIPTION')}</p>
						</div>
						<AuthActionComponent action="region_create">
							<Link to="create">
								<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
									{t('COMMON.ADD')}
								</Button>
							</Link>
						</AuthActionComponent>
					</header>

					<div style={{ position: 'relative' }}>
						<Table
							columns={columns}
							dataSource={regions}
							scroll={{ x: 600 }}
							rowKey="id"
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}
						/>

						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				confirmText={t('COMMON.DELETE')}
				onDiscard={onDiscardConfirmation}
				title={t('REGIONS.DELETE_TITLE')}
				description={t('REGIONS.DELETE_DESCRIPTION')}
				note={t('REGIONS.DELETE_NOTE')}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListRegionsPage;
